import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { initGA } from '@/utils/analytics';

const COOKIE_CONSENT_KEY = 'portico_cookie_consent';

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();
  const onDashboard = location.pathname.startsWith('/dashboard');

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (consent === 'accepted') {
      // User already accepted previously — boot GA on this page load
      initGA();
    }
    if (!consent && !onDashboard) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [onDashboard]);

  const accept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setVisible(false);
    // Fire-up analytics now that consent has been granted
    initGA();
  };

  const decline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
          data-testid="cookie-consent-banner"
        >
          <div className="max-w-4xl mx-auto bg-card border border-border rounded-2xl shadow-2xl p-5 md:p-6">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex w-10 h-10 rounded-full bg-primary/10 items-center justify-center flex-shrink-0 mt-0.5">
                <Cookie className="w-5 h-5 text-primary" />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-semibold text-foreground text-sm mb-1">
                  We Value Your Privacy
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  We use cookies and similar technologies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All," you consent to our use of cookies. Read our{' '}
                  <Link to="/privacy" className="text-primary hover:underline font-medium">
                    Privacy Policy
                  </Link>{' '}
                  for more details.
                </p>
              </div>

              <button
                onClick={decline}
                className="p-1.5 rounded-full hover:bg-muted transition-colors flex-shrink-0 sm:hidden"
                aria-label="Close cookie banner"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            <div className="flex items-center justify-end gap-3 mt-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={decline}
                className="text-xs"
                data-testid="cookie-decline-btn"
              >
                Decline
              </Button>
              <Button
                size="sm"
                onClick={accept}
                className="text-xs px-5"
                data-testid="cookie-accept-btn"
              >
                Accept All
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
